import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, SvgIcon } from "@material-ui/core";
import moment from "moment";
import { useHistory } from 'react-router-dom';
import CalendarToday from '@material-ui/icons/CalendarToday';
import { numberAsCurrency } from '../utils';
import FederalDrawInfo from './FederalDrawInfo';

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: '16px',
    backgroundColor: '#F2F2F2',
    border: '1px solid #E3E3E3',
    marginBottom: '12px',
    display: 'grid',
    gridTemplateColumns: '76px minmax(0, 1fr) minmax(92px, auto)',
    columnGap: '10px',
    alignItems: 'stretch',
    cursor: 'pointer',
    padding: '10px 12px',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '76px minmax(0, 1fr) minmax(92px, auto)',
      columnGap: '10px',
      padding: '10px 12px',
    },
    '&:hover': {
      boxShadow: '0 3px 10px rgba(0, 0, 0, 0.06)',
    },
  },
  rootFirst: {
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    marginBottom: '10px',
    cursor: 'pointer',
  },
  imageContainer: {
    flexShrink: 0,
    width: '76px',
    height: '76px',
    borderRadius: '12px',
    backgroundColor: '#DCDCDC',
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      padding: '8px',
      boxSizing: 'border-box',
    },
    [theme.breakpoints.down('xs')]: {
      width: '76px',
      height: '76px',
      borderRadius: '12px',
      '& img': {
        padding: '8px',
      },
    },
  },
  imageContainerFirst: {
    flexShrink: 0,
    width: '64px',
    height: '64px',
    margin: '14px 12px 14px 16px',
    borderRadius: '50%',
    backgroundColor: '#E8F0FE',
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  details: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '6px',
    textAlign: 'left',
  },
  rightCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '6px',
    minWidth: 0,
  },
  title: {
    fontWeight: 700,
    color: '#111111',
    fontSize: '1.05rem',
    lineHeight: 1.1,
    overflow: 'visible',
    wordBreak: 'break-word',
    letterSpacing: '-0.01em',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.05rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.05rem',
    },
  },
  subtitle: {
    color: '#333333',
    fontWeight: 700,
    textTransform: 'uppercase',
    fontSize: '0.78rem',
    lineHeight: 1.08,
    overflow: 'visible',
    wordBreak: 'break-word',
    letterSpacing: '0.01em',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.78rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.78rem',
    },
  },
  prizeValue: {
    color: '#2D6E2F',
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: 1.1,
    margin: 0,
  },
  drawNumber: {
    color: '#1A1A1A',
    fontSize: '0.82rem',
    fontWeight: 500,
    lineHeight: 1.1,
    margin: 0,
    '& strong': {
      fontWeight: 700,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.82rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.82rem',
    },
  },
  ticketBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#E9F6E9',
    borderRadius: '12px',
    padding: '4px 8px',
    color: '#2D6E2F',
    fontSize: '0.72rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.72rem',
      padding: '4px 8px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.72rem',
      padding: '4px 8px',
    },
  },
  ticketIcon: {
    fontSize: '0.9rem',
    color: '#2D6E2F',
    fill: '#2D6E2F',
    flexShrink: 0,
    '& path': {
      fill: '#2D6E2F',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9rem',
    },
  },
  ticketNumber: {
    display: 'inline-block',
    lineHeight: 1.1,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#1A1A1A',
    fontSize: '0.9rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9rem',
      gap: '4px',
    },
  },
  metaIcon: {
    fontSize: '0.95rem',
    color: '#1A1A1A',
    fill: '#1A1A1A',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.95rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.95rem',
    },
  },
}));

export default function Winner({winner, currentUser, influencer, mode = 'list'}) {
  const classes = useStyles();
  const history = useHistory();
  moment.locale('pt-BR');
  const subtitleSource = winner?.subtitle || winner?.title || '';
  const cleanedSubtitle = subtitleSource
    .replace(/translation missing:[\s\S]*$/i, '')
    .trim();
  const subtitle = cleanedSubtitle || '';

  function handleClick() {
    history.push(`/${influencer.username}/site/campanhas/${winner.id}`);
  }

  const toEntity = winner.drawn_to_entity;

  return (
    <div className={mode == 'list' ? classes.root : classes.rootFirst} onClick={handleClick}>
      <div className={mode == 'list' ? classes.imageContainer : classes.imageContainerFirst}>
        <img src={(!toEntity && winner.winner_photo_url) || "https://d2zdlj1ifv8u1d.cloudfront.net/images/coelho.jpg"} />
      </div>
      <div className={mode == 'list' ? classes.details : classes.detailsFirst}>
        <Typography className={classes.title} component="h5" variant="h5">
          {toEntity
            ? 'Prêmio da entidade (venda mínima não atingida)'
            : `${winner.drawn_customer?.name || ''}${winner.drawn_customer?.cpf ? ` (${winner.drawn_customer.cpf})` : ''}`}
        </Typography>
        {subtitle && (
          <Typography className={classes.subtitle} variant="body2">
            {subtitle}
          </Typography>
        )}
        {winner?.prize_value && (
          <Typography className={classes.prizeValue} variant="body2">
            {numberAsCurrency(winner.prize_value)}
          </Typography>
        )}
        {winner?.federal_draw_label ? (
          <FederalDrawInfo campaign={winner} />
        ) : winner?.draw_number ? (
          <Typography className={classes.drawNumber} variant="body2">
            Sorteio: <strong>{winner.draw_number}</strong>
          </Typography>
        ) : null}
      </div>
      <div className={classes.rightCol}>
        <span className={classes.ticketBadge}>
          <SvgIcon className={classes.ticketIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M22 10V6c0-1.11-.9-2-2-2H4c-1.1 0-1.99.89-1.99 2v4c1.1 0 1.99.9 1.99 2s-.89 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2s.9-2 2-2zm-9 7.5h-2v-2h2v2zm0-4.5h-2v-2h2v2zm0-4.5h-2v-2h2v2z"></path>
          </SvgIcon>
          <span className={classes.ticketNumber}>{winner.drawn_ticket_number}</span>
        </span>
        <span className={classes.metaItem}>
          <CalendarToday className={classes.metaIcon} />
          {moment(winner.draw_time).format("DD/MM")}
        </span>
      </div>
    </div>
  );
}

